import { useState, useEffect, useRef } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { InstallPWA } from "@/components/InstallPWA";
import { ForcePasswordChange } from "@/components/ChangePassword";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  LayoutDashboard, Users, CalendarDays, UserCog, PoundSterling,
  BookOpen, LogOut, Menu, X, Receipt, TriangleAlert, UserPlus, History, Inbox, ClipboardCheck, Trash2, Plane, UserCircle, ShieldCheck, GraduationCap,
} from "lucide-react";
import { pageOf } from "@/lib/permissions";

const MASTER_NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/roster", label: "Rostering", icon: CalendarDays },
  { to: "/enquiries", label: "Enquiries", icon: ClipboardCheck },
  { to: "/clients", label: "Clients", icon: Users },
  { to: "/staff", label: "Befrienders", icon: UserCog },
  { to: "/incidents", label: "Incidents", icon: TriangleAlert },
  { to: "/billing", label: "Billing", icon: Receipt },
  { to: "/payroll", label: "Payroll", icon: PoundSterling },
  { to: "/families", label: "Family Access", icon: UserPlus },
  { to: "/activity", label: "Activity log", icon: History },
  { to: "/policies", label: "Policies", icon: BookOpen },
  { to: "/compliance", label: "Compliance", icon: ShieldCheck },
  { to: "/training", label: "Training", icon: GraduationCap },
  { to: "/inbox", label: "Inbox", icon: Inbox },
  { to: "/leave", label: "Leave & holidays", icon: Plane },
  { to: "/trash", label: "Recycle bin", icon: Trash2 },
];

const CARER_NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/roster", label: "My Visits", icon: CalendarDays },
  { to: "/incidents", label: "Incidents", icon: TriangleAlert },
  { to: "/policies", label: "Policies", icon: BookOpen },
  { to: "/compliance", label: "My Compliance", icon: ShieldCheck },
  { to: "/training", label: "My Training", icon: GraduationCap },
  { to: "/inbox", label: "Inbox", icon: Inbox },
  { to: "/leave", label: "Leave & holidays", icon: Plane },
];

export default function Layout({ children }) {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [shift, setShift] = useState({ shift_over: true, shift_end: null });
  const [unread, setUnread] = useState(0);
  const perms = user?.permissions || [];
  const baseNav = user?.role === "carer"
    ? CARER_NAV
    : MASTER_NAV.filter((item) => perms.includes(pageOf(item.to)));
  const nav = user?.role === "family" ? baseNav : [...baseNav, { to: "/account", label: "My account", icon: UserCircle }];

  useEffect(() => {
    if (user?.role === "carer") api.get("/me/shift-status").then((r) => setShift(r.data)).catch(() => {});
  }, [user?.role]);

  useEffect(() => {
    api.get("/messages/unread-count").then((r) => setUnread(r.data.unread || 0)).catch(() => {});
  }, [user?.id]);

  // Near-real-time check-in / check-out alerts for the office & admin.
  const seenRef = useRef(null);
  useEffect(() => {
    if (!["admin", "office"].includes(user?.role)) return;
    seenRef.current = new Date().toISOString();
    const poll = () => {
      api.get("/messages/unread-count").then((r) => setUnread(r.data.unread || 0)).catch(() => {});
      api.get(`/messages/live?after=${encodeURIComponent(seenRef.current)}`).then((r) => {
        (r.data || []).forEach((m) => {
          toast(m.subject, { description: m.body, duration: 8000 });
          if (m.created_at > seenRef.current) seenRef.current = m.created_at;
        });
      }).catch(() => {});
    };
    const t = setInterval(poll, 15000);
    return () => clearInterval(t);
  }, [user?.role, user?.id]);

  const handleLogout = () => {
    if (user?.role === "carer" && shift.shift_end && !shift.shift_over) {
      toast.error(`You can sign out once your shift ends at ${shift.shift_end}.`);
      return;
    }
    logout();
    navigate("/login");
  };

  const SidebarContent = () => (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-3 px-6 py-6">
        <img src="/logo.png" alt="Brightside" className="h-10 w-10 rounded-xl object-cover shadow-lg shadow-black/20" />
        <div className="leading-tight">
          <p className="font-heading text-base font-bold text-white">Brightside</p>
          <p className="text-[11px] tracking-wide text-white/50">Befriending Services</p>
        </div>
      </div>
      <nav className="mt-2 flex-1 space-y-1 px-3">
        {nav.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            onClick={() => setOpen(false)}
            data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors duration-150 ${
                isActive
                  ? "bg-gradient-to-r from-sage to-mint text-white shadow-sm shadow-sage/30"
                  : "text-white/70 hover:bg-white/10 hover:text-white"
              }`
            }
          >
            <item.icon size={18} />
            <span className="flex-1">{item.label}</span>
            {item.to === "/inbox" && unread > 0 && (
              <span data-testid="inbox-unread-badge" className="rounded-full bg-coral px-2 py-0.5 text-[11px] font-bold text-white">{unread}</span>
            )}
          </NavLink>
        ))}
      </nav>
      <div className="border-t border-white/10 p-3">
        <InstallPWA />
        {user?.role === "family" ? (
          <div className="mb-2 px-3 py-2">
            <p className="truncate text-sm font-semibold text-white">{user?.name}</p>
            <p className="truncate text-xs capitalize text-white/50">{user?.role === "carer" ? "befriender" : user?.role}</p>
          </div>
        ) : (
          <NavLink to="/account" onClick={() => setOpen(false)} data-testid="sidebar-profile-link"
            className="mb-2 flex items-center gap-3 rounded-lg px-3 py-2 transition-colors hover:bg-white/10">
            {user?.photo
              ? <img src={user.photo} alt={user?.name} className="h-9 w-9 shrink-0 rounded-full object-cover ring-2 ring-white/20" />
              : <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white/15 text-sm font-bold text-white">{(user?.name || "?").charAt(0)}</span>}
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-white">{user?.name}</p>
              <p className="truncate text-xs capitalize text-white/50">{user?.role === "carer" ? "befriender" : user?.role} · Edit profile</p>
            </div>
          </NavLink>
        )}
        <button
          onClick={handleLogout}
          data-testid="logout-button"
          className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-white/70 transition-colors hover:bg-coral/20 hover:text-white"
        >
          <LogOut size={18} /> Sign out
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-ivory">
      {user?.must_change_password && <ForcePasswordChange />}
      {/* Desktop sidebar */}
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-64 bg-gradient-to-b from-sidebar via-sidebar to-brand-dark lg:block">
        <SidebarContent />
      </aside>

      {/* Mobile top bar */}
      <div className="sticky top-0 z-30 flex items-center justify-between border-b border-brand/10 bg-white/95 px-4 py-3 backdrop-blur-md lg:hidden">
        <div className="flex items-center gap-2">
          <img src="/logo.png" alt="Brightside" className="h-8 w-8 rounded-lg object-cover" />
          <span className="font-heading font-bold text-ink">Brightside</span>
        </div>
        <button onClick={() => setOpen(true)} data-testid="mobile-menu-button" className="rounded-lg p-2 text-ink">
          <Menu size={22} />
        </button>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <div className="absolute inset-y-0 left-0 w-64 bg-gradient-to-b from-sidebar via-sidebar to-brand-dark">
            <button onClick={() => setOpen(false)} className="absolute right-3 top-4 text-white/70">
              <X size={22} />
            </button>
            <SidebarContent />
          </div>
        </div>
      )}

      <main className="lg:pl-64">
        <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-10 lg:py-10">{children}</div>
      </main>
    </div>
  );
}
